import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { BottomNav } from '@/components/navigation/BottomNav/BottomNav.jsx';
import { useUIStore } from '@/stores';
import styles from './AppShell.module.css';

/**
 * AppShell - Shared application shell
 * 
 * Wraps all pages with:
 * - Global navigation (bottom tab bar)
 * - Page transition handling
 * - Scroll management
 * - Global UI overlays (toasts, modals)
 */
export function AppShell() {
  const location = useLocation();
  const { setPreviousRoute, addToast } = useUIStore();

  // Track route changes
  useEffect(() => {
    setPreviousRoute(location.pathname);
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location.pathname, setPreviousRoute]);

  // Determine if we should show bottom nav
  // Hide on landing, auth, onboarding, and immersive detail pages
  const hideNavPaths = ['/', '/welcome', '/login', '/signup'];
  const isDetailPage = location.pathname.match(/^\/(venues|events)\/[^/]+$/);
  const showNav = !hideNavPaths.includes(location.pathname) && !isDetailPage;

  return (
    <div className={styles.appShell}>
      {/* Main content area */}
      <main className={styles.main}>
        <Outlet />
      </main>

      {/* Bottom navigation */}
      {showNav && <BottomNav />}

      {/* Toast container */}
      <ToastContainer />
    </div>
  );
}

/**
 * ToastContainer - Renders active toasts
 */
function ToastContainer() {
  const { toasts, removeToast } = useUIStore();

  if (toasts.length === 0) return null;

  return (
    <div className={styles.toastContainer}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`${styles.toast} ${styles[toast.type]}`}
          onClick={() => removeToast(toast.id)}
        >
          {toast.message}
        </div>
      ))}
    </div>
  );
}

export default AppShell;
